import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "option"];
import { useQuery } from 'data-fetching-client';
import { useMemo } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedMessageWithObjectTypeName from 'automation-ui-i18n/FormattedMessageWithObjectTypeName';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UISelect from 'UIComponents/input/UISelect';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import { LIST_DATASETS } from 'automation-ui-enrollment/queries/DatasetQueries';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DatasetOptionComponent = _ref => {
  let {
      children,
      option
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx("span", Object.assign({}, rest, {
    children: /*#__PURE__*/_jsx(UITooltip, {
      title: option.disabledReason,
      disabled: !option.disabled,
      children: children
    })
  }));
};
const DatasetSelect = ({
  objectTypeId,
  selectedDatasetId,
  selectedSubviewId,
  onDatasetSelect,
  readOnly,
  label
}) => {
  const {
    loading,
    error,
    data
  } = useQuery(LIST_DATASETS);
  const toOptionValue = ({
    datasetId,
    subviewId
  }) => {
    if (!datasetId) {
      return undefined;
    }
    return `${datasetId}:${subviewId !== null && subviewId !== void 0 ? subviewId : ''}`;
  };
  const fromOptionValue = value => {
    const [datasetId, subviewId] = value.split(':');
    return {
      datasetId: parseInt(datasetId, 10),
      subviewId: subviewId || null
    };
  };
  const onChange = ({
    target: {
      value
    }
  }) => {
    var _fromOptionValue;
    const {
      datasetId,
      subviewId
    } = (_fromOptionValue = fromOptionValue(value)) !== null && _fromOptionValue !== void 0 ? _fromOptionValue : {};
    onDatasetSelect(syntheticEvent({
      datasetId,
      subviewId
    }));
  };
  const options = useMemo(() => {
    var _data$listDatasets$re, _data$listDatasets;
    const processDataset = dataset => {
      var _dataset$datasetDefin, _dataset$subviews;
      // Dataset-specific disabled check
      const disabled = objectTypeId ? ((_dataset$datasetDefin = dataset.datasetDefinition) === null || _dataset$datasetDefin === void 0 || (_dataset$datasetDefin = _dataset$datasetDefin.table) === null || _dataset$datasetDefin === void 0 ? void 0 : _dataset$datasetDefin.objectTypeId) !== objectTypeId : false;
      const disabledReason = disabled && objectTypeId ? /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
        baseLangKey: "automation-ui-enrollment.redesign.enrollExistingPanel.enrollFromDataset.datasetTypeDisabledReason_cob",
        objectTypeId: objectTypeId
      }) : undefined;
      const result = [{
        text: dataset.name,
        value: toOptionValue({
          datasetId: dataset.id,
          subviewId: ''
        }),
        disabled,
        disabledReason
      }];

      // Process subviews if they exist
      if ((_dataset$subviews = dataset.subviews) !== null && _dataset$subviews !== void 0 && _dataset$subviews.length) {
        dataset.subviews.forEach(subview => {
          const subviewDisabled = !!objectTypeId && !subview.objectsEligibleForSingleObjectActivations.includes(objectTypeId);
          const subviewDisabledReason = subviewDisabled && objectTypeId ? /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
            baseLangKey: "automation-ui-enrollment.redesign.enrollExistingPanel.enrollFromDataset.datasetTypeDisabledReason_cob",
            objectTypeId: objectTypeId
          }) : undefined;
          result.push({
            text: subview.name,
            value: toOptionValue({
              datasetId: dataset.id,
              subviewId: subview.id
            }),
            disabled: subviewDisabled,
            disabledReason: subviewDisabledReason
          });
        });
      }
      return result;
    };
    return (_data$listDatasets$re = data === null || data === void 0 || (_data$listDatasets = data.listDatasets) === null || _data$listDatasets === void 0 ? void 0 : _data$listDatasets.reduce((acc, dataset) => {
      var _dataset$datasetDefin2;
      if ((_dataset$datasetDefin2 = dataset.datasetDefinition) !== null && _dataset$datasetDefin2 !== void 0 && (_dataset$datasetDefin2 = _dataset$datasetDefin2.table) !== null && _dataset$datasetDefin2 !== void 0 && _dataset$datasetDefin2.objectTypeId) {
        acc.push(...processDataset(dataset));
      }
      return acc;
    }, [])) !== null && _data$listDatasets$re !== void 0 ? _data$listDatasets$re : [];
  }, [data === null || data === void 0 ? void 0 : data.listDatasets, objectTypeId]);
  return /*#__PURE__*/_jsx(UIFormControl, {
    label: label !== null && label !== void 0 ? label : objectTypeId ? /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
      baseLangKey: "automation-ui-enrollment.redesign.enrollExistingPanel.enrollFromDataset.selected.datasetType_cob",
      objectTypeId: objectTypeId
    }) : null,
    error: !!error,
    validationMessage: error && /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-enrollment.redesign.enrollExistingPanel.enrollFromDataset.datasetsError"
    }),
    children: /*#__PURE__*/_jsx(_Fragment, {
      children: /*#__PURE__*/_jsx(UISelect, {
        readOnly: readOnly,
        disabled: !!error,
        onChange: onChange,
        placeholder: I18n.text('automation-ui-enrollment.generic-enrollment-table.select-dataset-placeholder'),
        isLoading: loading || !data,
        value: toOptionValue({
          datasetId: selectedDatasetId,
          subviewId: selectedSubviewId
        }),
        options: options,
        itemComponent: DatasetOptionComponent
      })
    })
  });
};
export default DatasetSelect;